import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { newsService } from '@/services/storage';
import type { News } from '@/types';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<News | null>(null);
  const [relatedNews, setRelatedNews] = useState<News[]>([]);

  useEffect(() => {
    const loadNews = async () => {
      if (id) {
        const item = await newsService.getById(id);
        if (item) {
          setNews(item);
          // Get related news
          const allNews = await newsService.getAll();
          setRelatedNews(
            allNews
              .filter((n) => n.id !== id && n.category === item.category)
              .slice(0, 3)
          );
        }
      }
    };
    loadNews();
  }, [id]);

  if (!news) {
    return (
      <div className="pt-32 pb-20 text-center">
        <div className="text-gray-500">新闻不存在</div>
        <Button asChild className="mt-4">
          <Link to="/news">返回新闻列表</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Breadcrumb */}
      <section className="bg-gray-50 py-4 border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600">
              首页
            </Link>
            <span className="mx-2">/</span>
            <Link to="/news" className="hover:text-blue-600">
              新闻中心
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{news.title}</span>
          </div>
        </div>
      </section>

      {/* Article Header */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/news"
              className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-6"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              返回新闻列表
            </Link>

            <Badge className="mb-4">
              {news.category === 'company' ? '公司动态' : '行业新闻'}
            </Badge>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {news.title}
            </h1>

            <div className="flex items-center justify-between text-sm text-gray-500 mb-8">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {news.date}
              </div>
              <Button variant="ghost" size="sm">
                <Share2 className="h-4 w-4 mr-1" />
                分享
              </Button>
            </div>

            {/* Featured Image */}
            <div className="aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={news.image || `https://images.unsplash.com/photo-1519389950476-29a8e752cc80?w=800&q=80`}
                alt={news.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1519389950476-29a8e752cc80?w=800&q=80';
                }}
              />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 leading-relaxed">
                {news.summary}
              </p>
              <div 
                className="text-gray-600 leading-relaxed mt-4 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: news.content || '' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Related News */}
      {relatedNews.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">相关新闻</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedNews.map((item) => (
                <Link key={item.id} to={`/news/${item.id}`}>
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-video bg-gray-200">
                      <img
                        src={item.image || `https://images.unsplash.com/photo-1519389950476-29a8e752cc80?w=400&q=80`}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1519389950476-29a8e752cc80?w=400&q=80';
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="text-sm text-gray-500 mb-2">{item.date}</div>
                      <h3 className="font-bold text-gray-900 line-clamp-2">{item.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
